/* One material of one lesson, on paper. `/tisk/<lekce>/<kind>`

   The lesson page prints too — print.css opens every fold, so a teacher who
   presses Ctrl+P there gets the goal and all ten materials. That is the right
   sheet for preparing; it is the wrong one for the classroom. What goes on the
   desks is *one* material, and nobody hands out nine others to get at it.

   So this route exists, and it exists only where there is something to print:
   the material has to be finished and carried as blocks. A recording has
   nothing to print, an application is a file, and a material that has not been
   made yet would print a blank page under a title — which reads as a broken
   sheet rather than as an honest gap. All three answer 404, and nothing links
   to them: the `K tisku` mark on the kit row becomes a link exactly when it
   leads somewhere.

   The head of the sheet is for the teacher, not the class: which lesson this
   belongs to, how long it takes and which levels of the goal it serves. It is
   four lines, because the sheet is going to be copied and the copier does not
   care about our design.

   OWNED BY ITEM 13 OF PLAN.md. */

import { useEffect } from "react";
import { useParams } from "react-router-dom";

import { Blocks } from "../../blocks";
import { T } from "../../blocks/shared";
import { useI18n } from "../../lib/i18n";
import { useDoc } from "../../lib/hooks";
import { GOAL_LEVELS, isAbsent, isLesson } from "../../lib/types";
import type { KitItemPresent } from "../../lib/types";
import { LoadFailed, NotFound } from "../NotFound";

export function LessonSheet() {
  const { id = "", kind = "" } = useParams();
  const { t, ui } = useI18n();
  const { data: doc, error, loading } = useDoc(id);

  /* Same as the handout routes: no header, no footer, nothing that is not the
     sheet itself. */
  useEffect(() => {
    document.body.classList.add("printing");
    return () => document.body.classList.remove("printing");
  }, []);

  if (loading) return null;
  // A document that is not a lesson is the address being wrong, not the
  // server being unreachable, so that half stays a plain not-found.
  if (error) return <LoadFailed error={error} />;
  if (!doc || !isLesson(doc)) return <NotFound />;

  const item = doc.kit.find((k) => k.kind === kind);
  if (!item || isAbsent(item)) return <NotFound />;

  const ready = (item as KitItemPresent).ready;
  if (ready?.as !== "blocks") return <NotFound />;

  const one = item as KitItemPresent;
  const served = GOAL_LEVELS.filter((l) => one.levels.includes(l));

  return (
    <div className="handout">
      {/* `last`, because there is only one. Without it print.css breaks the page
          after the sheet and every material comes out with a blank second page
          — which is the very spill the handouts are built to avoid. */}
      <section className="sheet kit-sheet last">
        <div className="eyebrow">{t(doc.title)}</div>
        <h2>{t(one.title)}</h2>
        <div className="lead"><T value={one.claim} /></div>
        <p className="kit-sheet-meta">
          <span>{ui(`kit.${one.kind}`)}</span>
          {one.minutes ? <span>{one.minutes} {ui("lesson.minutes")}</span> : null}
          <span>{ui("lesson.levels")}: {served.map((l) => ui(`level.${l}`)).join(", ")}</span>
        </p>
        <Blocks blocks={ready.blocks} />
      </section>
    </div>
  );
}
